import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Visible TV cursor. It follows `pointer move` messages from the phone with a
// soft ease-out so fast finger movements don't jump.

const HIDE_AFTER_MS = 1600;
const MIN_STEP = 0.0015;

const RemotePointerOverlay = () => {
  const location = useLocation();
  const targetRef = useRef({ x: 0.5, y: 0.5 });
  const currentRef = useRef({ x: 0.5, y: 0.5 });
  const rafRef = useRef(0);
  const hideTimer = useRef(null);
  const [visible, setVisible] = useState(false);
  const [cursor, setCursor] = useState({ x: 50, y: 50 });

  useEffect(() => {
    const scheduleHide = () => {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = window.setTimeout(() => setVisible(false), HIDE_AFTER_MS);
    };

    const tick = () => {
      rafRef.current = 0;
      const target = targetRef.current;
      const current = currentRef.current;
      const dx = target.x - current.x;
      const dy = target.y - current.y;

      if (Math.abs(dx) + Math.abs(dy) < MIN_STEP) {
        currentRef.current = { ...target };
        setCursor({ x: target.x * 100, y: target.y * 100 });
        return;
      }

      currentRef.current = {
        x: current.x + dx * 0.28,
        y: current.y + dy * 0.28,
      };
      setCursor({
        x: currentRef.current.x * 100,
        y: currentRef.current.y * 100,
      });
      rafRef.current = window.requestAnimationFrame(tick);
    };

    const scheduleTick = () => {
      if (!rafRef.current) rafRef.current = window.requestAnimationFrame(tick);
    };

    const onMessage = (event) => {
      let fromParent = false;
      try {
        fromParent = window.parent && window.parent !== window && event.source === window.parent;
      } catch (err) {
        fromParent = false;
      }
      if (!fromParent) return;

      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.source !== 'flexwatch-remote' || data.type !== 'pointer') return;

      const x = Number(data.x);
      const y = Number(data.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      targetRef.current = {
        x: Math.min(1, Math.max(0, x)),
        y: Math.min(1, Math.max(0, y)),
      };
      setVisible(true);
      scheduleHide();
      scheduleTick();
    };

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(hideTimer.current);
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    };
  }, []);

  if (location.pathname.startsWith('/watch')) return null;

  return (
    <div
      aria-hidden="true"
      className={`fixed z-[1000002] pointer-events-none transition-opacity duration-300 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{
        left: `${cursor.x}vw`,
        top: `${cursor.y}vh`,
        transform: 'translate(-2px, -2px)',
      }}
    >
      <svg width="26" height="26" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M3 2 L25 13.4 L14.2 15.1 L19.2 25.6 L14.1 26.8 L9.4 16.3 L3 21.5 Z"
          fill="#ffffff"
          stroke="#050709"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

export default RemotePointerOverlay;
